import { Injectable } from '@angular/core';
import { getJSONSchemaURL } from '@y42/feature/json-schema';
import { CanvasElementType, CanvasFilterSettings, CanvasLayer, CanvasWidgetSettings } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish } from '@y42/util/nullish';
import { v4 as uuid } from 'uuid';
import {
  canvasElementTypeToModelCreator,
  CanvasFilterStoreModel,
  CanvasWidgetStoreModel,
  defaultCanvasObjectProperties,
  isStickyItem,
} from '../../state/canvas-element/models';
import { CanvasElement } from '../../state/canvas-element/visualization-canvas-element.model';
import { VisualizationCanvasElementQuery } from '../../state/canvas-element/visualization-canvas-element.query';
import { VisualizationCanvasElementStore } from '../../state/canvas-element/visualization-canvas-element.store';
import { VisualizationDashboardQuery } from '../../state/dashboard';
import { VisualizationFilterQuery, VisualizationFilterStore, VisualizationFilterStoreModel } from '../../state/filter';
import { VisualizationTabQuery, VisualizationTabStore, VisualizationTabStoreModel } from '../../state/tab';
import { VisualizationWidgetQuery, VisualizationWidgetStore, VisualizationWidgetStoreModel } from '../../state/widget';
import { VisualizationFilterService } from '../filter/visualization-filter.service';
import { VisualizationWidgetService } from '../widget/visualization-widget.service';
import { CanvasZOrderService } from './services/canvas-z-order.service';

@Injectable({ providedIn: 'root' })
export class VisualizationCanvasElementService {
  constructor(
    private readonly store: VisualizationCanvasElementStore,
    private readonly query: VisualizationCanvasElementQuery,
    private readonly dashboardQuery: VisualizationDashboardQuery,
    private readonly tabQuery: VisualizationTabQuery,
    private readonly tabStore: VisualizationTabStore,
    private readonly widgetQuery: VisualizationWidgetQuery,
    private readonly widgetStore: VisualizationWidgetStore,
    private readonly widgetService: VisualizationWidgetService,
    private readonly filterQuery: VisualizationFilterQuery,
    private readonly filterStore: VisualizationFilterStore,
    private readonly filterService: VisualizationFilterService,
    private readonly canvasZOrderService: CanvasZOrderService,
  ) {}

  create<T extends CanvasElementType>(
    canvasElementType: T,
    typeSettings: Parameters<(typeof canvasElementTypeToModelCreator)[T]>[0],
    select = false,
    visualizationName = this.dashboardQuery.getActive()!.$name,
    tabPath = this.tabQuery.getActiveId()!,
  ): CanvasElement<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const canvasElementSettings = canvasElementTypeToModelCreator[canvasElementType](typeSettings as any);

    const model = {
      ...canvasElementSettings,
      type: canvasElementType,
    };

    if (
      [
        CanvasElementType.Attention,
        CanvasElementType.Comment,
        CanvasElementType.Emoji,
        CanvasElementType.Picture,
        CanvasElementType.StickyNote,
      ].includes(canvasElementType)
    ) {
      model.position.x -= model.size.width / 2;
      model.position.y -= model.size.height / 2;
    }

    const [canvasElement] = this.createFromStoreModel(
      model as PartialWithRequired<CanvasElement<T>, 'type'>,
      select,
      visualizationName,
      tabPath,
    );

    assertNonNullable(canvasElement);
    this.tabStore.setSkipSync(true);
    this.tabStore.update(tabPath, (st: VisualizationTabStoreModel) => ({
      $children: [...(st.$children || []), canvasElement.$path],
    }));
    this.tabStore.setSkipSync(false);

    return canvasElement as CanvasElement<T>;
  }

  async update<T extends CanvasElementType>(ids: OrArray<string>, fn: (state: CanvasElement<T>) => CanvasElement<T>) {
    const updateFn = fn as (state: CanvasElement) => CanvasElement<T>;

    this.store.update(ids, updateFn);
    await this.store.pending;
  }

  async duplicate({
    paths,
    parentTab,
    updateSelection,
    parentDashboardName: targetDashboardName,
    shallowCopy = false,
  }: CanvasElementDuplicationParams) {
    // TODO:
    // Create everything at once instead of calling store.create() every time.
    const duplicatedItemsPaths = await Promise.all(
      paths
        .map((path) => this.query.getEntity(path))
        .filter(isNotNullish)
        .map(async (canvasItem) => {
          const sourceWidget = this.widgetQuery.getItem(
            (canvasItem as CanvasWidgetStoreModel).typeSettings?.widgetPath ?? '',
          );
          const sourceFilter = this.filterQuery.getEntity(
            (canvasItem as CanvasFilterStoreModel).typeSettings?.filterPath ?? '',
          );

          const typeSettings = { ...canvasItem.typeSettings };

          // Create a new instance of widget/filter for a deeply copied canvas widget/filter element.
          if (sourceWidget && !shallowCopy) {
            const widget = await this.widgetService.duplicate(sourceWidget);

            (typeSettings as CanvasWidgetSettings).widgetPath = widget.$path;
          } else if (sourceFilter && !shallowCopy) {
            const filter = await this.filterService.duplicate(sourceFilter);

            (typeSettings as CanvasFilterSettings).filterPath = filter.$path;
          }

          const newCanvasItem = await this.createFromStoreModel(
            {
              ...canvasItem,
              // Slightly change the position of pasted object.
              position: { x: canvasItem.position.x + 40, y: canvasItem.position.y + 40 },
              typeSettings,
            },
            false,
            targetDashboardName,
            parentTab,
          )[0];

          assertNonNullable(newCanvasItem);
          return newCanvasItem.$path;
        }),
    );

    const tabPath = parentTab ?? this.tabQuery.getActiveId()!;

    this.tabStore.setSkipSync(true);
    this.tabStore.update(tabPath, (st: VisualizationTabStoreModel) => ({
      $children: [...(st.$children || []), ...duplicatedItemsPaths],
    }));
    this.tabStore.setSkipSync(false);

    if (updateSelection) {
      this.updateCurrentSelectionIds(duplicatedItemsPaths);
    }

    return duplicatedItemsPaths;
  }

  delete(paths: OrArray<CanvasElement['$path']>) {
    if (!Array.isArray(paths)) {
      paths = [paths];
    }
    const filterIds = paths
      .map(($path) => this.query.getEntity($path))
      .filter((element) => element?.type === CanvasElementType.Filter)
      .reduce((_: Array<VisualizationFilterStoreModel['$name']>, element) => {
        const filterId = (element! as CanvasFilterStoreModel).typeSettings?.filterPath;

        return [filterId!];
      }, []);

    this.filterStore.delete(filterIds);

    // For widget elements, remove the referenced widget if it's not linked to any other canvas element
    const singleReferencedWidgets = paths
      .map(($path) => this.query.getEntity($path))
      .filter((element) => element?.type === CanvasElementType.Widget)
      .reduce((acc: Array<VisualizationWidgetStoreModel['$name']>, element) => {
        const widgetId: string | undefined = (element! as CanvasWidgetStoreModel).typeSettings?.widgetPath;
        const elementsUsingThisWidget = this.query
          .getAll()
          .filter(
            (e) =>
              widgetId &&
              e.type === CanvasElementType.Widget &&
              (e as CanvasWidgetStoreModel).typeSettings?.widgetPath === widgetId,
          );
        if (elementsUsingThisWidget.length >= 2) {
          return acc;
        }
        return [...acc, widgetId!];
      }, []);

    this.widgetStore.delete(singleReferencedWidgets);

    const attachedItems = this.query
      .getAll()
      .filter((e) => isStickyItem(e) && e.typeSettings?.attachedTo && paths.includes(e.typeSettings.attachedTo))
      .map((e) => e.$path);

    this.store.delete([...paths, ...attachedItems]);
  }

  async moveToTab(paths: OrArray<CanvasElement['$path']>, parentTab: VisualizationTabStoreModel['$path']) {
    if (!Array.isArray(paths)) {
      paths = [paths];
    }

    await this.duplicate({ paths: paths, parentTab, shallowCopy: true });

    this.delete(paths);
  }

  updateCurrentSelectionIds(currentSelectionIds: string[]) {
    this.store.updateCurrentSelectionIds(currentSelectionIds);
  }

  updateClipboard(clipboard?: CanvasElement[]) {
    this.store.updateClipboard(clipboard);
  }

  private createFromStoreModel<T extends CanvasElementType>(
    models: OrArray<PartialWithRequired<CanvasElement<T>, 'type'>>,
    select = false,
    dashboardName = this.dashboardQuery.getActive()!.$name,
    tabPath = this.tabQuery.getActiveId()!,
  ): Array<CanvasElement<T>> {
    if (!Array.isArray(models)) {
      models = [models];
    }

    const canvasElements = models.map((model) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const canvasElementModel = canvasElementTypeToModelCreator[model.type](model as any);

      // TODO: The fact that both "canvasElementModel" amd "model" are spread here seems incorrect and needs some investigation.
      return this.store.create({
        // ATTENTION
        // Order of properties is important!
        ...canvasElementModel,
        angle: 0,
        layer: CanvasLayer.Default,
        locked: false,
        typeSettings: null,
        objectProperties: defaultCanvasObjectProperties,

        ...model,

        // Make sure that the new canvas element will be placed on top of other elements of the same type.
        zIndex: this.canvasZOrderService.getHighestZOrder((canvasElementModel as CanvasElement).layer) + 1,

        // StoreModel properties need to be last (after spreading `...element`)
        $name: `${uuid()}.json`,
        $parent: tabPath,
        $schema: getJSONSchemaURL('visualization-canvas-element'),
        $parentFolders: [dashboardName, tabPath?.split('/').pop() || this.tabQuery.getActive()!.$name],
      }) as CanvasElement<T>;
    });

    if (select) {
      this.store.updateCurrentSelectionIds(canvasElements.map((model) => model.$path));
    }

    return canvasElements;
  }
}

type OrArray<Type> = Type | Type[];
type PartialWithRequired<Type, Required extends keyof Type> = Partial<Type> & Pick<Type, Required>;

interface CanvasElementDuplicationParams {
  paths: Array<CanvasElement['$path']>;
  parentTab?: VisualizationTabStoreModel['$path'];
  updateSelection?: boolean;
  parentDashboardName?: VisualizationTabStoreModel['$name'];
  /**
   * When set to `true` keep the original references to widgets and filters.
   */
  shallowCopy?: boolean;
}
